import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HiAcademicCap, HiOfficeBuilding, HiHeart,
  HiShoppingCart, HiTrendingUp, HiCreditCard, HiPlus, HiMinus
} from 'react-icons/hi';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { INDUSTRIES } from '../utils/constants';

const iconComponents = {
  GraduationCap: HiAcademicCap,
  Building: HiOfficeBuilding,
  Heart: HiHeart,
  ShoppingCart: HiShoppingCart,
  TrendingUp: HiTrendingUp,
  CreditCard: HiCreditCard,
};

const IndustriesSection = () => {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="industries"
      ref={sectionRef}
      style={{
        backgroundColor: '#F5F0E8',
        padding: 'clamp(4rem, 8vw, 7rem) 1.5rem',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Two-column layout like Anthropic's "Our policy priorities" */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
        }} className="industries-grid">
          {/* Left heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: '#1A1A1A',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              Industries we serve
            </h2>
            <p style={{
              marginTop: '1rem',
              fontSize: '1rem',
              lineHeight: 1.65,
              color: '#666666',
              maxWidth: '400px',
              fontFamily: '"DM Sans", sans-serif',
            }}>
              We understand the unique workflows of different sectors across India.
            </p>
          </motion.div>

          {/* Right: accordion items */}
          <div>
            {INDUSTRIES.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={sectionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05, ease: 'easeOut' }}
                style={{ borderBottom: '1px solid var(--color-border)' }}
              >
                <button
                  onClick={() => setOpenIndex(prev => prev === index ? null : index)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    padding: '1.25rem 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: '#1A1A1A',
                    fontFamily: '"DM Sans", sans-serif',
                  }}>
                    {industry.name}
                  </span>
                  {openIndex === index ? (
                    <HiMinus size={20} color="#666666" style={{ flexShrink: 0 }} />
                  ) : (
                    <HiPlus size={20} color="#666666" style={{ flexShrink: 0 }} />
                  )}
                </button>
                {openIndex === index && (
                  <div style={{ paddingBottom: '1.25rem', paddingRight: '2rem' }}>
                    <p style={{
                      fontSize: '0.9rem',
                      lineHeight: 1.65,
                      color: '#666666',
                      marginBottom: '0.75rem',
                      fontFamily: '"DM Sans", sans-serif',
                    }}>
                      {industry.description}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {industry.automations.map((a, i) => (
                        <li key={i} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                          fontSize: '0.85rem',
                          color: '#666666',
                          marginBottom: '0.375rem',
                          fontFamily: '"DM Sans", sans-serif',
                        }}>
                          <span style={{
                            display: 'inline-block',
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            backgroundColor: '#1A1A1A',
                            marginTop: '0.5rem',
                            flexShrink: 0,
                          }} />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .industries-grid {
            grid-template-columns: 1fr 1.5fr !important;
            gap: 5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default IndustriesSection;
